/**
 * Honest station-start failures (Ella worker contract 1.3.0).
 *
 * When Ella cannot find a single streamable track, stage-1 answers 503 with a `reasonCode`
 * telling us why. We show Ella's own words for it (translated) and the one action that helps,
 * instead of a spinner that never ends or a "try again" that can never work.
 */

export type NoTracksReasonCode = 'no_streaming_service' | 'region_unavailable' | 'no_tracks_found';

export type StationFailureAction = 'connect_qobuz' | 'back' | 'retry';

export const NO_TRACKS_MESSAGE_KEYS: Record<NoTracksReasonCode, string> = {
  no_streaming_service: 'ella_radio_no_streaming_service',
  region_unavailable: 'ella_radio_region_unavailable',
  no_tracks_found: 'ella_radio_no_tracks_found',
};

export const STATION_FAILURE_ACTION_LABEL_KEYS: Record<StationFailureAction, string> = {
  connect_qobuz: 'ella_radio_connect_qobuz',
  back: 'ella_radio_try_another_station',
  retry: 'ella_radio_retry_build',
};

export function isNoTracksReasonCode(value: unknown): value is NoTracksReasonCode {
  return value === 'no_streaming_service' || value === 'region_unavailable' || value === 'no_tracks_found';
}

export type ResolvedStationFailure = {
  message: string;
  reasonCode?: NoTracksReasonCode;
  action: StationFailureAction;
};

export function resolveStationFailure(
  failure: { message?: string; reasonCode?: string | null },
  t: (key: string, params?: Record<string, unknown>) => string,
): ResolvedStationFailure {
  const code = failure.reasonCode;
  if (isNoTracksReasonCode(code)) {
    return {
      message: t(NO_TRACKS_MESSAGE_KEYS[code]),
      reasonCode: code,
      action: code === 'no_streaming_service' ? 'connect_qobuz' : 'back',
    };
  }
  return { message: failure.message || t('ella_radio_no_tracks_found'), action: 'retry' };
}

/**
 * `api.post` throws `API <status>: <body>` on non-2xx. When the body is a structured
 * Ella failure envelope (`success: false`), return it so the caller can show the reason.
 */
export function structuredFailureFromApiError(err: unknown): Record<string, unknown> | null {
  const msg = String((err as Error)?.message || '');
  const match = /^API (\d{3}): ([\s\S]*)$/.exec(msg);
  if (!match) return null;
  const status = Number(match[1]);
  if (status < 500) return null;
  try {
    const body = JSON.parse(match[2]) as unknown;
    if (body && typeof body === 'object' && !Array.isArray(body)
      && (body as Record<string, unknown>).success === false) {
      return body as Record<string, unknown>;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function isStage1UnsupportedError(err: unknown): boolean {
  const msg = String((err as Error)?.message || err || '');
  return msg.includes('API 404') || msg.includes('API 405');
}

/**
 * Stage-1 fetch error → `null` when the worker lacks the endpoint (404/405, caller falls back
 * to legacy generate), the structured envelope for an honest 5xx, otherwise rethrow.
 */
export function stage1ErrorResultOrThrow(err: unknown): Record<string, unknown> | null {
  if (isStage1UnsupportedError(err)) return null;
  const structured = structuredFailureFromApiError(err);
  if (structured) return structured;
  throw err;
}
